// @ts-nocheck
import { useState, useEffect } from 'react';
import { IconLoader2 } from '@tabler/icons-react';
import Fetch from '@/utils/Fetch';
import analyticsService from '@/services/analytics/analytics-service';
import { decodeTokenPayload } from '@/utils/czTokenUtils';
import CustomerZoneHero from './CustomerZoneHero';
import CustomerZoneFooter from './CustomerZoneFooter';
import styles from './CustomerZoneHome.module.css';

type Stage = 'idle' | 'email_sent' | 'tenant_notified' | 'no_email';

interface Props {
  tokenStr: string | null;
  tenant: any;
  companyName: string;
  tenantId?: string;
  customerId?: string;
  legacy?: boolean;
}

export default function CustomerZoneExpiredLink({ tokenStr, tenant, companyName, tenantId, customerId, legacy }: Props) {
  const [stage, setStage] = useState<Stage>('idle');
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const decoded = tokenStr ? decodeTokenPayload(tokenStr) : null;
  const resolvedTenantId = decoded?.tenantId ?? tenantId;
  const resolvedCustomerId = decoded?.customerId ?? customerId;

  useEffect(() => {
    analyticsService.track('cz_link_expired', {
      portal: 'customerzone',
      ...(resolvedTenantId ? { tenant_id: resolvedTenantId } : {}),
      ...(resolvedCustomerId ? { customer_id: resolvedCustomerId } : {}),
      ...(decoded?.customerName ? { customer_name: decoded.customerName } : {}),
      legacy: !!legacy,
    });
  }, []);

  const handleRequest = async () => {
    if (stage !== 'idle' || isLoading || !tenant) return;
    analyticsService.track('cz_new_link_requested', {
      portal: 'customerzone',
      ...(resolvedTenantId ? { tenant_id: resolvedTenantId } : {}),
      ...(resolvedCustomerId ? { customer_id: resolvedCustomerId } : {}),
      ...(decoded?.customerName ? { customer_name: decoded.customerName } : {}),
      legacy: !!legacy,
    });
    setIsLoading(true);
    try {
      const body = tokenStr
        ? { expiredToken: tokenStr }
        : { tenantId, customerId };
      const res: any = await Fetch.post({
        url: '/CustomerZone/RequestNewLink',
        params: body,
        apiUrlOverride: tenant.API,
      });
      if (res?.method === 'email') {
        setMaskedEmail(res.maskedEmail ?? null);
        setStage('email_sent');
      } else if (res?.method === 'tenant_notified') setStage('tenant_notified');
      else setStage('no_email');
    } catch {
      setStage('no_email');
    } finally {
      setIsLoading(false);
    }
  };

  const display = companyName || 'the company';

  return (
    <div className={styles.root} style={{ display: 'flex', flexDirection: 'column' }}>
      <CustomerZoneHero companyName={companyName} logoUrl={null} expanded={false} />
      <div className={styles.shell} style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 440 }}>

            <p style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              {legacy ? 'Your link is no longer valid.' : 'Your link has expired.'}
            </p>
            {legacy && (
              <p style={{ color: '#666', marginBottom: '0.5rem' }}>
                Request a new link to access your Customer Zone.
              </p>
            )}

            {stage === 'idle' && (
              <>
                <button
                  onClick={handleRequest}
                  disabled={!tenant || isLoading}
                  style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.85rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    color: '#fff',
                    background: 'var(--blue-primary-color)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isLoading ? 'default' : 'pointer',
                    opacity: !tenant ? 0.5 : 1,
                  }}
                >
                  {isLoading && <IconLoader2 size={18} className={styles.spinner} />}
                  {isLoading ? 'Sending…' : 'Request a new link'}
                </button>
              </>
            )}

            {stage === 'email_sent' && (
              <p style={{ marginTop: '1rem' }}>
                A new link has been sent to {maskedEmail || 'your email address'}.
              </p>
            )}

            {stage === 'tenant_notified' && (
              <p style={{ marginTop: '1rem' }}>
                We&rsquo;ve notified <strong>{display}</strong>. They&rsquo;ll be in touch with
                your new link shortly.
              </p>
            )}

            {stage === 'no_email' && (
              <>
                <p style={{ marginTop: '1rem', marginBottom: '0.75rem' }}>
                  We don&rsquo;t have an email address on file for you.
                </p>
                <p style={{ color: '#666' }}>
                  Please contact <strong>{display}</strong> directly and ask them to send
                  you a new link.
                </p>
              </>
            )}

        </div>
      </div>
      <CustomerZoneFooter />
    </div>
  );
}
